import { Link } from 'react-router-dom';
import { mediaAsset } from '../config/media';

const applications = [
  ['01', 'Playback for Web', 'High-quality video playback, conferencing, audio metering, telestration, and session controls.'],
  ['02', 'Playback for iPhone + iPad', 'A React Native client with native media integrations for mobile playback.'],
  ['03', 'Playback for Apple TV', 'A television interface optimized for remote viewing and control.'],
  ['04', 'remotopro.io', 'The customer website, ecommerce flow, help center, and onboarding experience.'],
  ['05', 'Enterprise Admin', 'Account, company, room, user, and usage administration for enterprise customers.'],
  ['06', 'Internal Portal', 'Audit, support, data-export, and super-administration tools for internal operations.'],
];

const progression = [
  ['DAW integration consultant', 'Advised on professional-audio workflows and integration requirements.'],
  ['Product design + UX', 'Worked with Business Analysis to translate those workflows into product behavior.'],
  ['Project manager', 'Spent approximately 18 months coordinating requirements, design, priorities, and delivery.'],
  ['Web developer', 'Stepped into implementation when the browser client required both frontend and audiovisual expertise.'],
  ['Multi-application engineer', 'Built the customer site, administration tools, iOS client, and Apple TV client.'],
  ['Engineering lead', 'Own all six applications, lead web development, and delegate work across iOS and Apple TV.'],
];

function StudySection({ eyebrow, title, children, className = '' }) {
  return (
    <section className={`remote-study__section ${className}`.trim()}>
      <div className="remote-study__heading">
        <p className="portfolio-eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
      </div>
      <div className="remote-study__body">{children}</div>
    </section>
  );
}

function FlowNode({ eyebrow, title, children }) {
  return (
    <div className="remote-diagram__node">
      {eyebrow && <span>{eyebrow}</span>}
      <strong>{title}</strong>
      {children && <small>{children}</small>}
    </div>
  );
}

function TelestrationDiagram() {
  return (
    <figure className="remote-diagram" aria-labelledby="telestration-diagram-caption">
      <div className="remote-diagram__flow">
        <FlowNode eyebrow="Input" title="Pointer input">Mouse and touch coordinates</FlowNode>
        <span className="remote-diagram__arrow" aria-hidden="true">→</span>
        <FlowNode eyebrow="Transform" title="Normalize + batch">Viewport and letterbox correction</FlowNode>
        <span className="remote-diagram__arrow" aria-hidden="true">→</span>
        <FlowNode eyebrow="Transport" title="Realtime or relay">Throttled stroke messages</FlowNode>
        <span className="remote-diagram__arrow" aria-hidden="true">→</span>
        <FlowNode eyebrow="Playback" title="Dedupe + render">Aligned, fading strokes</FlowNode>
      </div>
      <figcaption id="telestration-diagram-caption">One drawing path, kept spatially consistent across different screens and network routes.</figcaption>
    </figure>
  );
}

function OnboardingDiagram() {
  return (
    <figure className="remote-diagram" aria-labelledby="onboarding-diagram-caption">
      <div className="remote-diagram__flow">
        <FlowNode eyebrow="Entry" title="Invitation or direct visit" />
        <span className="remote-diagram__arrow" aria-hidden="true">→</span>
        <FlowNode eyebrow="Resolve" title="Account + access">Login, subscription, session state</FlowNode>
        <span className="remote-diagram__arrow" aria-hidden="true">→</span>
        <div className="remote-diagram__branch">
          <FlowNode eyebrow="Web" title="Browser playback" />
          <FlowNode eyebrow="Native" title="App handoff" />
          <FlowNode eyebrow="Support" title="Recovery path" />
        </div>
        <span className="remote-diagram__arrow" aria-hidden="true">→</span>
        <FlowNode eyebrow="Verify" title="79 scenarios">Three apps · ~30 minute regression run</FlowNode>
      </div>
      <figcaption id="onboarding-diagram-caption">A single entry point resolves identity, entitlement, session, device, and stream conditions into a tested destination.</figcaption>
    </figure>
  );
}

function CreditArchitectureDiagram() {
  return (
    <figure className="remote-diagram" aria-labelledby="credits-diagram-caption">
      <div className="remote-diagram__credit-grid">
        <div className="remote-diagram__balances">
          <FlowNode eyebrow="Used first" title="Plan time">Monthly allowance</FlowNode>
          <FlowNode eyebrow="Held separately" title="Purchased time">Overages and one-off use</FlowNode>
        </div>
        <span className="remote-diagram__arrow" aria-hidden="true">→</span>
        <FlowNode eyebrow="Runtime" title="Session reservation">Reserve · extend · release · charge</FlowNode>
        <span className="remote-diagram__arrow" aria-hidden="true">→</span>
        <div className="remote-diagram__branch">
          <FlowNode eyebrow="Self-service" title="Stripe account" />
          <FlowNode eyebrow="Enterprise" title="Company account">Seats · roles · shared usage</FlowNode>
        </div>
      </div>
      <div className="remote-diagram__ledger" aria-label="Usage ledger events">
        <strong>Immutable usage ledger</strong>
        <span>Grant</span><span>Purchase</span><span>Reservation</span><span>Release</span><span>Charge</span>
      </div>
      <div className="remote-diagram__visibility" aria-label="Usage reporting outputs">
        <FlowNode eyebrow="Customer visibility" title="Enterprise usage dashboard">Company, seat, and product usage</FlowNode>
        <FlowNode eyebrow="Internal visibility" title="Sales analytics dashboard">Individual and enterprise usage</FlowNode>
      </div>
      <figcaption id="credits-diagram-caption">Two independent balances feed a shared reservation and ledger model, which also powers customer-facing and internal usage reporting.</figcaption>
    </figure>
  );
}

export default function RemoteStudioCaseStudy() {
  return (
    <main id="main-content" className="portfolio-page remote-study">
      <header className="portfolio-page-intro remote-study__intro">
        <p className="portfolio-eyebrow">Product engineering leadership · Real-time media</p>
        <h1>Remoto</h1>
        <p>
          How a consulting role in professional audio grew into ownership of six web, iOS, and Apple TV applications.
        </p>
      </header>

      <section className="case-study-summary remote-study__summary" aria-label="Remoto project summary">
        <div>
          <p className="portfolio-eyebrow">Role progression</p>
          <p>DAW Consultant → Product & UX → Project Manager → Head of Web & Mobile Engineering</p>
        </div>
        <div>
          <p className="portfolio-eyebrow">Period</p>
          <p>2020–Present</p>
        </div>
        <div>
          <p className="portfolio-eyebrow">Focus</p>
          <p>Product systems, frontend architecture, real-time media, prototyping, and technical leadership</p>
        </div>
      </section>

      <figure className="case-study-hero remote-study__product-hero">
        <div className="remote-study__product-brand">
          <img src={mediaAsset('images/remote-studio/remoto-system-logo.svg')} alt="Remoto" />
          <img src={mediaAsset('images/remote-studio/remoto-playback-logo.png')} alt="Remoto Playback" />
        </div>
        <img
          className="remote-study__product-screen"
          src={mediaAsset('images/remote-studio/remoto-playback-web.webp')}
          alt="Remoto Playback web interface with high-quality video and a participant video call"
        />
        <figcaption>Remoto Playback for Web combines media review, conferencing, session controls, and collaboration tools.</figcaption>
      </figure>

      <section className="remote-study__metrics" aria-label="Selected Remoto outcomes">
        <article><strong>6</strong><span>Production applications built and owned</span></article>
        <article><strong>46</strong><span>Major features delivered July 2025–July 2026</span></article>
        <article><strong>79</strong><span>Automated onboarding scenarios</span></article>
        <article><strong>70h → 30m</strong><span>Regression testing per release</span></article>
      </section>

      <StudySection eyebrow="Context" title="One connected product ecosystem">
        <p>
          Remoto makes collaboration tools for editors, producers, and mix engineers. A typical session crosses several parts of the product: buying and configuring service, inviting collaborators, joining from different devices, watching and discussing high-quality media, and managing the people and time involved.
        </p>
        <div className="remote-study__applications">
          {applications.map(([number, title, description]) => (
            <article key={title}>
              <span>{number}</span>
              <h3>{title}</h3>
              <p>{description}</p>
            </article>
          ))}
        </div>
        <figure className="remote-study__product-video">
          <video
            controls
            playsInline
            preload="metadata"
            poster={mediaAsset('images/remote-studio/remoto-playback-web.webp')}
          >
            <source src={mediaAsset('images/remote-studio/remoto-audio-eq-demo.mp4')} type="video/mp4" />
            Your browser does not support embedded video.
          </video>
          <figcaption>Audio metering and equalization controls in Remoto Playback.</figcaption>
        </figure>
        <aside className="remote-study__boundary">
          <p className="portfolio-eyebrow">Ownership boundary</p>
          <p>
            Remoto also has native macOS and Windows clients. I did not write code for them. I reviewed their UI and UX, helped approve product decisions, and worked with our CTO on the technical direction shared across the platform.
          </p>
        </aside>
      </StudySection>

      <StudySection eyebrow="Role" title="From domain consultant to engineering lead" className="remote-study__section--dark">
        <p>
          I first joined the company as a consultant on digital audio workstation integrations. Since I had worked in professional audio, I could explain how editors and mix engineers would actually use the product and where an idea might break down in a real session. I later joined the Business Analysis team’s product and UX work, then spent about a year and a half managing the project.
        </p>
        <p>
          The move into development was practical. We could not find a web developer who also understood the audiovisual side well enough to build the browser playback client, so I took it on. After that I built the customer site and ecommerce flow, the enterprise and internal admin tools, and finally the iOS and Apple TV apps.
        </p>
        <ol className="remote-study__timeline">
          {progression.map(([title, description], index) => (
            <li key={title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div><h3>{title}</h3><p>{description}</p></div>
            </li>
          ))}
        </ol>
        <p>
          I work closely with our CTO, Business Analysis team, and UI designer. I usually get involved early to test whether a feature is feasible, turn Figma files into working prototypes, and then build the production version. A second engineer joined after the first products launched and reports to me. I still own all six applications, but now review their work and delegate features across iOS and Apple TV while leading the web work directly.
        </p>
      </StudySection>

      <StudySection eyebrow="Technical deep dive 01" title="Collaborative telestration">
        <p>
          Keeping collaborative annotations aligned across different displays, source resolutions, and input devices is a distributed graphics problem. I designed and built Web Playback's implementation around that constraint. Pointer input is converted into normalized coordinates, with additional correction for Retina displays and the empty space introduced when video is letterboxed.
        </p>
        <p>
          Sending every point as it was drawn produced too much traffic, so I batched points and throttled updates. Messages can travel through the real-time connection or a server relay. Since a client may see the same message twice, the receiver keeps a small deduplication cache. Finished strokes fade on their own, and the controls support keyboard input and assistive technology.
        </p>
        <TelestrationDiagram />
      </StudySection>

      <StudySection eyebrow="Technical deep dive 02" title="Native SRT playback from React Native" className="remote-study__split">
        <div>
          <p>
            We wanted to share UI code between the iOS and Apple TV apps, but React Native did not provide the media features we needed. I wrote Objective-C++ and Swift modules that make our SRT/GStreamer player available to the TypeScript layer.
          </p>
          <p>
            The bridge handles connection state, native views, app lifecycle changes, audio layouts, frame rate, synchronization, and timing data. It also has to cleanly pass events between the native and JavaScript runtimes. This let us reuse the product UI across iPhone, iPad, and Apple TV without giving up control of the player.
          </p>
        </div>
        <figure className="remote-study__devices">
          <img
            src={mediaAsset('images/remote-studio/remoto-playback-devices.webp')}
            alt="Remoto Playback running across web, iPhone, and iPad during the same review session"
            loading="lazy"
          />
          <figcaption>A shared product experience across web, iPhone, and iPad.</figcaption>
        </figure>
      </StudySection>

      <StudySection eyebrow="Product systems 01" title="Onboarding as a system">
        <p>
          Session entry depends on the invitation, account and subscription state, session status, stream type, device, browser, the choice between web and native playback, and whether the session is an enterprise session.
        </p>
        <p>
          I designed and built this flow across the customer site and playback apps. It includes purchasing, account creation, invitations, device-aware routing, app handoff, help content, and session entry. Testing every path by hand took around 70 hours, so I built a visual automation harness that covers 79 scenarios across three applications. A regression run now takes about 30 minutes.
        </p>
        <OnboardingDiagram />
      </StudySection>

      <StudySection eyebrow="Product systems 02" title="A flexible usage-credit architecture" className="remote-study__section--accent">
        <p>
          Many competing services are sold through subscriptions and depend on dedicated hardware that customers must install and maintain. Remoto's software-only streaming and communication architecture lets us offer usage-based purchasing alongside subscriptions. That flexibility lowers the commitment required for smaller studios and freelance editors and gives the product a meaningful competitive advantage.
        </p>
        <p>
          I designed the original ecommerce model around two balances. One holds the hours included with a monthly plan. The other holds time bought separately for overages or one-off use. The backend team implemented the model in Java, and I built the purchasing, account, and in-session interfaces around it.
        </p>
        <p>
          Plan time is used first. Purchased time stays in its own balance and can be used when the plan runs out. The system reserves time as a session runs, can buy more automatically when the customer allows it, and keeps a ledger of each grant, purchase, reservation, release, and charge. Some of the original extension rules changed after customer feedback, but the two-balance model remained.
        </p>
        <p>
          Enterprise customers added another set of requirements. We kept the same underlying model, but added company ownership, seats, roles, shared usage, admin-only balance visibility, and access across multiple Remoto products. It now supports both self-service Stripe subscriptions and plans managed directly by Remoto.
        </p>
        <p>
          The same usage data powers two reporting experiences. Enterprise administrators have a dashboard for monitoring usage across their organization, while the Remoto Sales team has an analytics dashboard that provides visibility into product usage by individual and enterprise customers.
        </p>
        <CreditArchitectureDiagram />
      </StudySection>

      <StudySection eyebrow="Organizational leadership" title="Architecture, standards, and responsible AI" className="remote-study__section--standards">
        <p>
          As the number of applications grew, I established common patterns for typed services, hooks and providers, authentication, MFA, WebSocket connections, testing, and reusable controls. I worked with our CTO on the frontend stack, reviewed the desktop UX, and joined interviews for frontend and backend hires. Prototypes became our main way to test a customer request before committing the team to it.
        </p>
        <p>
          More recently, I have helped our CTO introduce AI into the development and design process. Several engineers had reasonable concerns about reliability, security, maintainability, and how the tools would affect their work. We tested specific uses, documented patterns, and kept human review explicit. We are treating possible AI features in the product as a separate roadmap, not as an excuse to add AI everywhere.
        </p>
      </StudySection>

      <StudySection eyebrow="Research" title="Prototypes and technical exploration" className="remote-study__prototypes">
        <article>
          <p className="portfolio-eyebrow">Prototype 01</p>
          <h3>Virtual Audio Bridge</h3>
          <p>A cross-platform exploration of multichannel virtual audio routing, timing, and native audio-device integration.</p>
        </article>
        <article>
          <p className="portfolio-eyebrow">Prototype 02</p>
          <h3>Timecode Toolkit</h3>
          <p>A portable C++ exploration of LTC and MIDI Time Code generation, conversion, and synchronization across native and browser-facing environments.</p>
        </article>
        <p className="remote-study__prototype-note">These are research prototypes, not shipped Remoto product features.</p>
      </StudySection>

      <StudySection eyebrow="Reflection" title="Building for a specialized market" className="remote-study__closing">
        <p>
          We built for one of the most demanding corners of remote collaboration. Audio and video post-production teams need high-quality media, dependable synchronization, stability under poor network conditions, strong security, and specialist features such as surround audio, Dolby Atmos support, and timecoded markers. They are also cautious about introducing new technology into established production workflows, and enterprise sales can take a year or longer.
        </p>
        <p>
          The market was smaller and slower-moving than the scope of the platform required. We were competing directly with ClearView, Louper, and Evercast for the same specialized buyers. At the same time, much of our underlying product—high-quality streaming, conferencing, annotation, session management, and cross-device access—addressed needs shared by advertising, social-content production, game development and streaming, podcasting, education, healthcare, and government.
        </p>
        <p>
          There is an upside to starting with the hardest customers. Remoto's MPA and TPN Gold certifications required the company to meet rigorous security and operational standards. Combined with the reliability work demanded by post-production, that gives the platform a credible foundation for other trust-sensitive markets, including government, education, and healthcare.
        </p>
        <p>
          Customer needs, expressed through direct requests, shaped the platform: the web, iOS, and Apple TV products were all built in response to customer demand. The ongoing sales effort is testing how broadly that demand can translate into adoption and support the full scope of the platform.
        </p>
        <div className="portfolio-actions">
          <Link className="portfolio-button" to="/contact">Start a conversation</Link>
          <Link className="portfolio-text-link" to="/work">Return to selected work</Link>
        </div>
      </StudySection>
    </main>
  );
}
